import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, User, CreditCard, Shield, Loader2, LogOut, ArrowUpRight, ArrowDownRight, XCircle, CheckCircle, FileText, HardDrive, BadgeCheck, Save, Lock, KeyRound, Monitor, Bell, Database, Download, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { toast } from "sonner";
import { authFetch, authLogout } from "@/lib/api";
import MyConventionSection from "@/components/MyConventionSection";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = {
  veyer_ciel_sub: { name: "Véyèr CIEL Pro", color: "#2D6A4F" },
  veyer_ciel_pro: { name: "Véyèr CIEL Pro", color: "#2D6A4F" },
  veyer_ciel: { name: "Véyèr CIEL (ancien plan)", color: "#2D6A4F" },
};

const PREF_DEFAULTS = { alertes_sanitaires: true, meteo: true, nouveautes: false };
const PREF_ITEMS = [
  { key: "alertes_sanitaires", title: "Alertes sanitaires", desc: "Foyer de bioagresseur detecte pres de vos parcelles" },
  { key: "meteo", title: "Bulletins meteo & agronomie", desc: "Resume hebdomadaire par e-mail" },
  { key: "nouveautes", title: "Nouveautes produit", desc: "Annonces et mises a jour Veyer CIEL" },
];

const mergePrefs = (p) => ({
  alertes_sanitaires: typeof p?.alertes_sanitaires === "boolean" ? p.alertes_sanitaires : PREF_DEFAULTS.alertes_sanitaires,
  meteo: typeof p?.meteo === "boolean" ? p.meteo : PREF_DEFAULTS.meteo,
  nouveautes: typeof p?.nouveautes === "boolean" ? p.nouveautes : PREF_DEFAULTS.nouveautes,
});

const getInitials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const fieldLabel = "text-[11px] font-semibold uppercase tracking-wide";

// Petit interrupteur reutilisable (aucune dependance ajoutee)
function Toggle({ on, onClick, disabled, testId }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} onClick={onClick} disabled={disabled}
      data-testid={testId}
      className="relative rounded-full transition-colors shrink-0 disabled:opacity-50"
      style={{ width: 42, height: 24, background: on ? "#2D6A4F" : "#D8D3C8" }}>
      <span className="absolute rounded-full bg-white transition-all"
        style={{ top: 2, left: on ? 20 : 2, width: 20, height: 20, boxShadow: "0 1px 2px rgba(0,0,0,.2)" }} />
    </button>
  );
}

export default function AccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [account, setAccount] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Informations personnelles
  const [profileForm, setProfileForm] = useState({ name: "", phone: "", region: "reunion" });
  const [savingProfile, setSavingProfile] = useState(false);

  // Securite
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPassword, setSavingPassword] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [revokingToken, setRevokingToken] = useState(null);

  // Preferences
  const [preferences, setPreferences] = useState(PREF_DEFAULTS);

  // RGPD
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchAccount = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/stripe/account-info`);
      if (!res.ok) throw new Error();
      setAccount(await res.json());
    } catch (err) {
      console.warn('[AccountPage] fetch failed, redirecting:', err);
      navigate("/", { replace: true });
    } finally { setLoading(false); }
  }, [navigate]);

  const fetchMe = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/auth/me`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMe(data);
      setProfileForm({
        name: data?.name || "",
        phone: data?.phone || "",
        region: data?.region === "mayotte" ? "mayotte" : "reunion",
      });
      setPreferences(mergePrefs(data?.preferences));
    } catch (err) {
      console.warn('[AccountPage] /auth/me failed:', err);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/profile/sessions`);
      if (!res.ok) throw new Error();
      setSessions(await res.json());
    } catch (err) {
      console.warn('[AccountPage] /profile/sessions failed:', err);
    }
  }, []);

  useEffect(() => {
    fetchAccount();
    fetchMe();
    fetchSessions();
    if (searchParams.get("upgraded")) {
      toast.success("Abonnement mis a jour !");
    }
  }, [fetchAccount, fetchMe, fetchSessions, searchParams]);

  const handleCancel = async () => {
    if (!window.confirm("Etes-vous sur de vouloir annuler votre abonnement ?")) return;
    setActionLoading("cancel");
    try {
      const res = await authFetch(`${API_BASE}/stripe/cancel-subscription`, { method: "POST" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(data.message);
      fetchAccount();
    } catch { toast.error("Erreur"); }
    setActionLoading(null);
  };

  const handleChangePlan = async (planId) => {
    setActionLoading(planId);
    try {
      const res = await authFetch(`${API_BASE}/stripe/change-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin, plan_id: planId }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.url) {
        const w = window.open(data.url, "_blank");
        if (!w) toast.info("Ouvrez le lien de paiement manuellement");
      }
    } catch { toast.error("Erreur"); }
    setActionLoading(null);
  };

  const handleLogout = async () => {
    try { await authLogout(); } catch {}
    navigate("/", { replace: true });
  };

  // --- Informations personnelles ---
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await authFetch(`${API_BASE}/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: profileForm.name, phone: profileForm.phone, region: profileForm.region }),
      });
      if (!res.ok) throw new Error();
      setMe((m) => ({ ...(m || {}), ...profileForm }));
      toast.success("Profil mis a jour");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    setSavingProfile(false);
  };

  // --- Securite ---
  const handleChangePassword = async () => {
    if (pwForm.new_password.length < 8) {
      toast.error("Le nouveau mot de passe doit contenir au moins 8 caracteres");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setSavingPassword(true);
    try {
      const res = await authFetch(`${API_BASE}/profile/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: pwForm.current_password, new_password: pwForm.new_password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data?.detail || "Erreur"); }
      else {
        toast.success(data?.message || "Mot de passe mis a jour");
        setPwForm({ current_password: "", new_password: "", confirm: "" });
      }
    } catch { toast.error("Erreur"); }
    setSavingPassword(false);
  };

  const handleRevokeSession = async (token) => {
    setRevokingToken(token);
    try {
      const res = await authFetch(`${API_BASE}/profile/sessions/${token}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setSessions((s) => s.filter((x) => x.session_token !== token));
      toast.success("Session deconnectee");
    } catch { toast.error("Erreur"); }
    setRevokingToken(null);
  };

  // --- Preferences ---
  const handleTogglePref = async (key) => {
    const prev = preferences;
    const next = { ...preferences, [key]: !preferences[key] };
    setPreferences(next);
    try {
      const res = await authFetch(`${API_BASE}/profile/preferences`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ preferences: next }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setPreferences(prev);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // --- RGPD ---
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await authFetch(`${API_BASE}/profile/export`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "veyerciel-mes-donnees.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export telecharge");
    } catch { toast.error("Erreur lors de l'export"); }
    setExporting(false);
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("Supprimer definitivement votre compte ? Vos donnees personnelles seront anonymisees. Cette action est irreversible.")) return;
    setDeleting(true);
    try {
      const res = await authFetch(`${API_BASE}/profile`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      toast.success("Compte supprime");
      try { await authLogout(); } catch {}
      navigate("/", { replace: true });
    } catch { toast.error("Erreur lors de la suppression"); setDeleting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F9F8F6" }}>
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#2D6A4F" }} />
    </div>
  );

  const plan = PLAN_LABELS[account?.subscription_plan] || null;
  const isActive = account?.subscription_status === "active" || account?.manual_access;
  const isCancelling = account?.subscription_status === "cancelling";
  const currentPlan = account?.subscription_plan;
  const storageMB = ((account?.storage_used_bytes || 0) / (1024 * 1024)).toFixed(1);

  const displayName = me?.name || account?.name || "";
  const displayEmail = me?.email || account?.email || "";
  const displayPicture = me?.picture || account?.picture;
  const isAdmin = me?.is_admin ?? account?.is_admin;

  return (
    <div className="min-h-screen" style={{ background: "#F9F8F6" }}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="rounded-xl" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold" style={{ color: "#1A1A1A", fontFamily: "Manrope, sans-serif" }}>Mon Compte</h1>
        </div>

        {/* Profile header */}
        <div className="rounded-2xl p-6 mb-4" style={{ background: "white", border: "1px solid #E5E5E5" }}>
          <div className="flex items-center gap-4">
            {displayPicture ? (
              <img src={displayPicture} alt="" className="w-14 h-14 rounded-full" />
            ) : (
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold" style={{ background: "#2D6A4F" }}>
                {displayName ? getInitials(displayName) : <User className="w-6 h-6 text-white" />}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-lg font-bold" style={{ color: "#1A1A1A" }}>{displayName}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm" style={{ color: "#7A7A7A" }}>{displayEmail}</p>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#DCFCE7", color: "#166534" }}>
                  <BadgeCheck className="w-3 h-3" /> Verifie
                </span>
              </div>
              {isAdmin && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1" style={{ background: "#8B5CF6", color: "white" }}>
                  <Shield className="w-3 h-3" /> Admin
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Convention : signature en autonomie + exemplaire signé */}
        <MyConventionSection />

        {/* Informations personnelles */}
        <div className="rounded-2xl p-6 mb-4" style={{ background: "white", border: "1px solid #E5E5E5" }}>
          <h2 className="text-base font-bold mb-4 flex items-center gap-2" style={{ color: "#1A1A1A" }}>
            <User className="w-5 h-5" style={{ color: "#2D6A4F" }} /> Informations personnelles
          </h2>
          <div className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className={fieldLabel} style={{ color: "#7A7A7A" }}>Nom complet</label>
              <Input value={profileForm.name} onChange={(e) => setProfileForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Votre nom" data-testid="profile-name-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className={fieldLabel} style={{ color: "#7A7A7A" }}>Telephone</label>
                <Input value={profileForm.phone} onChange={(e) => setProfileForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="+262 ..." data-testid="profile-phone-input" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className={fieldLabel} style={{ color: "#7A7A7A" }}>Region par defaut</label>
                <select value={profileForm.region} onChange={(e) => setProfileForm((f) => ({ ...f, region: e.target.value }))}
                  data-testid="profile-region-select"
                  className="flex h-9 w-full rounded-md border px-3 py-1 text-sm bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  style={{ borderColor: "#E5E5E5" }}>
                  <option value="reunion">La Reunion</option>
                  <option value="mayotte">Mayotte</option>
                </select>
              </div>
            </div>
            <Button size="sm" className="rounded-xl text-xs gap-1 mt-1" style={{ background: "#2D6A4F" }}
              onClick={handleSaveProfile} disabled={savingProfile} data-testid="save-profile-btn">
              {savingProfile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Enregistrer
            </Button>
          </div>
        </div>

        {/* Securite */}
        <div className="rounded-2xl p-6 mb-4" style={{ background: "white", border: "1px solid #E5E5E5" }}>
          <h2 className="text-base font-bold mb-4 flex items-center gap-2" style={{ color: "#1A1A1A" }}>
            <Lock className="w-5 h-5" style={{ color: "#2D6A4F" }} /> Securite
          </h2>
          <div className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className={fieldLabel} style={{ color: "#7A7A7A" }}>Mot de passe actuel</label>
              <PasswordInput value={pwForm.current_password}
                onChange={(e) => setPwForm((f) => ({ ...f, current_password: e.target.value }))}
                data-testid="current-password-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className={fieldLabel} style={{ color: "#7A7A7A" }}>Nouveau mot de passe</label>
                <PasswordInput value={pwForm.new_password}
                  onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))}
                  placeholder="8 caracteres min." data-testid="new-password-input" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className={fieldLabel} style={{ color: "#7A7A7A" }}>Confirmer</label>
                <PasswordInput value={pwForm.confirm}
                  onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                  data-testid="confirm-password-input" />
              </div>
            </div>
            <Button size="sm" className="rounded-xl text-xs gap-1 mt-1" style={{ background: "#2D6A4F" }}
              onClick={handleChangePassword} disabled={savingPassword} data-testid="change-password-btn">
              {savingPassword ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
              Mettre a jour le mot de passe
            </Button>
          </div>

          {/* Sessions actives */}
          <div className="mt-6 pt-4" style={{ borderTop: "1px solid #E5E5E5" }}>
            <h3 className="text-sm font-bold mb-1 flex items-center gap-2" style={{ color: "#1A1A1A" }}>
              <Monitor className="w-4 h-4" style={{ color: "#2D6A4F" }} /> Sessions actives
            </h3>
            {sessions.length === 0 ? (
              <p className="text-xs py-2" style={{ color: "#7A7A7A" }}>Aucune session a afficher.</p>
            ) : (
              <div>
                {sessions.map((s) => (
                  <div key={s.session_token} className="flex items-center gap-3 py-3" style={{ borderTop: "1px solid #E5E5E5" }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: "#1A1A1A" }}>{s.user_agent || "Appareil inconnu"}</p>
                      <p className="text-xs" style={{ color: "#7A7A7A" }}>
                        {s.ip ? `${s.ip}` : ""}{s.ip && s.last_seen ? " · " : ""}
                        {s.last_seen ? `vu le ${new Date(s.last_seen).toLocaleString("fr-FR")}` : ""}
                      </p>
                    </div>
                    {s.current ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0" style={{ background: "#DCFCE7", color: "#166534" }}>
                        <CheckCircle className="w-3 h-3" /> Actuelle
                      </span>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-xl text-xs gap-1 shrink-0"
                        onClick={() => handleRevokeSession(s.session_token)} disabled={revokingToken === s.session_token}
                        data-testid="revoke-session-btn">
                        {revokingToken === s.session_token ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
                        Deconnecter
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Preferences & notifications */}
        <div className="rounded-2xl p-6 mb-4" style={{ background: "white", border: "1px solid #E5E5E5" }}>
          <h2 className="text-base font-bold mb-2 flex items-center gap-2" style={{ color: "#1A1A1A" }}>
            <Bell className="w-5 h-5" style={{ color: "#2D6A4F" }} /> Preferences & notifications
          </h2>
          {PREF_ITEMS.map((item, i) => (
            <div key={item.key} className="flex items-center gap-3 py-3" style={i > 0 ? { borderTop: "1px solid #E5E5E5" } : undefined}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: "#1A1A1A" }}>{item.title}</p>
                <p className="text-xs" style={{ color: "#7A7A7A" }}>{item.desc}</p>
              </div>
              <Toggle on={!!preferences[item.key]} onClick={() => handleTogglePref(item.key)} testId={`pref-${item.key}-toggle`} />
            </div>
          ))}
        </div>

        {/* Subscription */}
        <div className="rounded-2xl p-6 mb-4" style={{ background: "white", border: "1px solid #E5E5E5" }}>
          <h2 className="text-base font-bold mb-4 flex items-center gap-2" style={{ color: "#1A1A1A" }}>
            <CreditCard className="w-5 h-5" style={{ color: "#2D6A4F" }} /> Abonnement
          </h2>

          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-semibold" style={{ color: plan?.color || "#7A7A7A" }}>
                {plan ? plan.name : "Aucun abonnement"}
              </p>
            </div>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full`}
              style={{
                background: isActive ? "#DCFCE7" : isCancelling ? "#FEF3C7" : "#FEE2E2",
                color: isActive ? "#166534" : isCancelling ? "#92400E" : "#991B1B",
              }} data-testid="subscription-status-badge">
              {isActive ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {isActive ? "Actif" : isCancelling ? "Annulation en cours" : "Inactif"}
            </span>
          </div>

          {account?.subscription_expires && (
            <p className="text-xs mb-4" style={{ color: "#7A7A7A" }}>
              Expire le : {new Date(account.subscription_expires).toLocaleDateString("fr-FR")}
            </p>
          )}

          {/* Actions */}
          {isActive && !account?.is_admin && (
            <div className="flex flex-wrap gap-2 mt-2">
              {currentPlan === "veyer_ciel" && (
                <Button size="sm" className="rounded-xl text-xs gap-1" style={{ background: "#8B5CF6" }}
                  onClick={() => handleChangePlan("veyer_ciel_pro")}
                  disabled={!!actionLoading} data-testid="upgrade-pro-btn">
                  {actionLoading === "veyer_ciel_pro" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
                  Passer en Pro
                </Button>
              )}
              {currentPlan === "veyer_ciel_pro" && (
                <Button size="sm" variant="outline" className="rounded-xl text-xs gap-1"
                  onClick={() => handleChangePlan("veyer_ciel")}
                  disabled={!!actionLoading} data-testid="downgrade-btn">
                  {actionLoading === "veyer_ciel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                  Revenir au plan standard
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-xl text-xs gap-1 border-red-200 text-red-600 hover:bg-red-50"
                onClick={handleCancel} disabled={!!actionLoading} data-testid="cancel-subscription-btn">
                {actionLoading === "cancel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                Annuler l'abonnement
              </Button>
            </div>
          )}

          {!isActive && !account?.is_admin && (
            <Button size="sm" className="rounded-xl text-xs gap-1" style={{ background: "#2D6A4F" }}
              onClick={() => navigate("/subscription")} data-testid="subscribe-btn">
              <CreditCard className="w-3.5 h-3.5" /> Choisir un abonnement
            </Button>
          )}
        </div>

        {/* Storage */}
        <div className="rounded-2xl p-6 mb-4" style={{ background: "white", border: "1px solid #E5E5E5" }}>
          <h2 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: "#1A1A1A" }}>
            <HardDrive className="w-5 h-5" style={{ color: "#2D6A4F" }} /> Stockage Documents
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold" style={{ color: "#1A1A1A" }}>{storageMB} Mo</p>
              <p className="text-xs" style={{ color: "#7A7A7A" }}>sur 100 Mo / parcelle</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold" style={{ color: "#2D6A4F" }}>{account?.document_count || 0}</p>
              <p className="text-xs" style={{ color: "#7A7A7A" }}>documents</p>
            </div>
          </div>
          {currentPlan !== "veyer_ciel_pro" && currentPlan !== "veyer_ciel_sub" && !account?.is_admin && !account?.manual_access && (
            <p className="text-[10px] mt-3 px-2 py-1 rounded-lg" style={{ background: "#FEF3C7", color: "#92400E" }}>
              <FileText className="w-3 h-3 inline mr-1" />
              L'upload de documents est reserve aux abonnes PRO
            </p>
          )}
        </div>

        {/* Mes donnees (RGPD) */}
        <div className="rounded-2xl p-6 mb-4" style={{ background: "white", border: "1px solid #E5E5E5" }}>
          <h2 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: "#1A1A1A" }}>
            <Database className="w-5 h-5" style={{ color: "#2D6A4F" }} /> Mes donnees
          </h2>
          <div className="flex items-center gap-3 py-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: "#1A1A1A" }}>Exporter mes donnees</p>
              <p className="text-xs" style={{ color: "#7A7A7A" }}>Profil, parcelles, carnet et registre (JSON)</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-xl text-xs gap-1 shrink-0"
              onClick={handleExport} disabled={exporting} data-testid="export-data-btn">
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Exporter
            </Button>
          </div>
          <div className="flex items-center gap-3 py-3" style={{ borderTop: "1px solid #E5E5E5" }}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-600">Supprimer mon compte</p>
              <p className="text-xs" style={{ color: "#7A7A7A" }}>Anonymisation ; le registre legal est conserve</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-xl text-xs gap-1 shrink-0 border-red-200 text-red-600 hover:bg-red-50"
              onClick={handleDeleteAccount} disabled={deleting} data-testid="delete-account-btn">
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Supprimer
            </Button>
          </div>
        </div>

        {/* Logout */}
        <Button variant="outline" className="w-full rounded-xl gap-2 text-red-600 border-red-200 hover:bg-red-50 mt-4"
          onClick={handleLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4" /> Se deconnecter
        </Button>
      </div>
    </div>
  );
}
